import React, { useState } from 'react'
import { useStyles } from './TemplateCardButtons.styles'
import clsx from 'clsx'
import { useStyles as commonStyles } from '../../FlyOutStorefront.styles'
import { Typography } from '@mui/material'
import {
  DragDropComponent,
  DropDownInput,
  SwitchV2,
  SwitchVarient,
} from '@labrav/react-components'
import { useFormikContext } from 'formik'
import { CustomFormModal } from './CustomFormModal/CustomFormModal'
import { ListingCardInCreation } from '../../../../modules/storefront/listingCardCreation/reducer'

export const TemplateCardButtons: React.FC = () => {
  const { classes } = useStyles()
  const { classes: commonClasses } = commonStyles()
  const { values, setFieldValue } = useFormikContext<ListingCardInCreation>()
  const { listingCardData } = values
  const [activeModal, setActiveModal] = useState<string | null>(null)

  const handleChange = (
    key: string,
    event: { target: { value: string } }
  ) => {
    const newFieldType = event.target.value as 'Outlined' | 'Filled'
    const { outlinedButton, filledButton } =
      values.listingCardData?.template?.data

    const updatedButtons = (
      values.listingCardData?.template?.data.buttons || []
    ).map(button => {
      if (button.key === key) {
        const customizationToApply =
          newFieldType === 'Outlined' ? outlinedButton : filledButton

        return {
          ...button,
          fieldType: newFieldType,
          customization: {
            ...customizationToApply,
          },
        }
      }
      return button
    })

    setFieldValue('listingCardData.template.data.buttons', updatedButtons)
  }

  const handleCloseModal = () => {
    setActiveModal(null)
  }

  const openModal = (modalName: string) => {
    setActiveModal(modalName)
  }

  const isValueChecked = (key: string): boolean => {
    return (
      listingCardData?.template?.data.buttons?.some(
        button => button.key === key && button.visibility
      ) || false
    )
  }

  const handleChangeSwitch = (key: string, title: string) => {
    const existingButtons = values.listingCardData?.template?.data.buttons || []
    const { outlinedButton, filledButton } =
      values.listingCardData?.template?.data

    const updatedButtons = existingButtons.map(button => {
      if (button.key === key) {
        const isVisible = !button.visibility
        const customizationToApply =
          isVisible && button.fieldType === 'Outlined'
            ? outlinedButton
            : isVisible && button.fieldType === 'Filled'
              ? filledButton
              : {}

        return {
          ...button,
          visibility: isVisible,
          customization: {
            ...customizationToApply,
          },
        }
      }
      return button
    })

    setFieldValue('listingCardData.template.data.buttons', updatedButtons)
  }

  const contentItems = [
    {
      id: 'buy_with_aws',
      content: (
        <div className={classes.rowContainer}>
          <div className={classes.rowInnerContainer}>
            <Typography className={classes.buttonTitle}>
              Buy with AWS
            </Typography>
            <SwitchV2
              data-testid="buy-with-aws-switch"
              checked={isValueChecked('buy_with_aws')}
              switchVarient={SwitchVarient.DARK}
              onChange={() =>
                handleChangeSwitch('buy_with_aws', 'Buy with AWS')
              }
            />
          </div>
          <DropDownInput
            title={'Buy with AWS'}
            dropDownFields={['Filled', 'Outlined']}
            onChange={(event: { target: { value: unknown } }) =>
              handleChange('buy_with_aws', {
                target: { value: String(event.target.value) },
              })
            }
            keyName={'buy_with_aws'}
          />
          <div>
            <Typography className={classes.buttonDescription}>
              Enable direct purchase
            </Typography>
          </div>
        </div>
      ),
    },
    {
      id: 'open_listing',
      content: (
        <div className={classes.rowContainer}>
          <div className={classes.rowInnerContainer}>
            <Typography className={classes.buttonTitle}>
              View on AWS Marketplace
            </Typography>
            <SwitchV2
              data-testid="view-on-aws-marketplace-switch"
              checked={isValueChecked('open_listing')}
              switchVarient={SwitchVarient.DARK}
              onChange={() =>
                handleChangeSwitch('open_listing', 'View on AWS Marketplace')
              }
            />
          </div>
          <DropDownInput
            title={'View on AWS Marketplace'}
            dropDownFields={['Outlined', 'Filled']}
            onChange={(event: { target: { value: unknown } }) =>
              handleChange('open_listing', {
                target: { value: String(event.target.value) },
              })
            }
            keyName={'open_listing'}
          />
        </div>
      ),
    },
    {
      id: 'request_private_offer',
      content: (
        <div className={classes.rowContainer}>
          <div className={classes.rowInnerContainer}>
            <Typography className={classes.buttonTitle}>
              Request a private offer
            </Typography>
            <SwitchV2
              data-testid="request-a-private-offer-switch"
              checked={isValueChecked('request_private_offer')}
              switchVarient={SwitchVarient.DARK}
              onChange={() =>
                handleChangeSwitch(
                  'request_private_offer',
                  'Request a private offer'
                )
              }
            />
          </div>
          <DropDownInput
            title={'Request a private offer'}
            dropDownFields={['Outlined', 'Filled']}
            onChange={(event: { target: { value: unknown } }) =>
              handleChange('request_private_offer', {
                target: { value: String(event.target.value) },
              })
            }
            keyName={'request_private_offer'}
          />
          <div>
            <Typography
              className={classes.linkText}
              onClick={() => openModal('privateOffer')}
            >
              Create a custom form for the user
            </Typography>
            <CustomFormModal
              keyName="privateOffer"
              open={activeModal === 'privateOffer'}
              handleClose={handleCloseModal}
              title="Request a private offer form"
            />
          </div>
        </div>
      ),
    },
    {
      id: 'request_demo',
      content: (
        <div className={classes.rowContainer}>
          <div className={classes.rowInnerContainer}>
            <Typography className={classes.buttonTitle}>
              Request a demo
            </Typography>
            <SwitchV2
              data-testid="request-a-demo-switch"
              checked={isValueChecked('request_demo')}
              switchVarient={SwitchVarient.DARK}
              onChange={() =>
                handleChangeSwitch('request_demo', 'Request a demo')
              }
            />
          </div>
          <DropDownInput
            title={'Request demo'}
            dropDownFields={['Outlined', 'Filled']}
            onChange={(event: { target: { value: unknown } }) =>
              handleChange('request_demo', {
                target: { value: String(event.target.value) },
              })
            }
            keyName={'request_demo'}
          />
          <div>
            <Typography
              className={classes.linkText}
              onClick={() => openModal('demo')}
            >
              Create a custom form for the user
            </Typography>
            <CustomFormModal
              keyName="demo"
              open={activeModal === 'demo'}
              handleClose={handleCloseModal}
              title="Request a demo form"
            />
          </div>
        </div>
      ),
    },
    {
      id: 'free_trial',
      content: (
        <div className={classes.rowContainer}>
          <div className={classes.rowInnerContainer}>
            <Typography className={classes.buttonTitle}>
              Try for free
            </Typography>
            <SwitchV2
              data-testid="try-for-free-switch"
              checked={isValueChecked('free_trial')}
              switchVarient={SwitchVarient.DARK}
              onChange={() => handleChangeSwitch('free_trial', 'Try for free')}
            />
          </div>
          <DropDownInput
            title={'Try for free'}
            dropDownFields={['Filled', 'Outlined']}
            onChange={(event: { target: { value: unknown } }) =>
              handleChange('free_trial', {
                target: { value: String(event.target.value) },
              })
            }
            keyName={'free_trial'}
          />
          <div>
            <Typography className={classes.buttonDescription}>
              Only applicable if free trial enabled
            </Typography>
          </div>
        </div>
      ),
    },
  ]

  const getContentById = (id: string) => {
    const item = contentItems.find(item => item.id === id)
    return item ? item.content : null
  }

  const dragDropItems = (listingCardData?.template?.data?.buttons || []).map(
    item => {
      return {
        id: item.key,
        key: item.key,
        content: getContentById(item.key),
        title: item.title,
        visibility: item.visibility,
        fieldType: item.fieldType,
        customization: item.customization,
      }
    }
  )

  const handleReorder = (
    reorderedItems: { id: string; content: React.ReactNode }[]
  ) => {
    const updatedButtons = reorderedItems.map(reorderedItem => {
      const matchedItem = dragDropItems.find(
        dragItem => dragItem.id === reorderedItem.id
      )
      return {
        key: matchedItem?.key || '',
        title: matchedItem?.title || '',
        visibility: matchedItem?.visibility || false,
        fieldType: matchedItem?.fieldType || 'Outlined',
        customization: matchedItem?.customization || {},
      }
    })
    setFieldValue('listingCardData.template.data.buttons', updatedButtons)
  }

  return (
    <div
      className={clsx(commonClasses.sectionWrapper, classes.cardHeadingWrapper)}
      data-testid="template-card-buttons-component"
    >
      <Typography
        // className={commonClasses.cardTextHeader}
        data-testid="template-card-buttons-title"
        className={classes.title}
      >
        Buttons
      </Typography>
      <div className={classes.buttonsContainer}>
        <div data-testid="drag-drop-component">
          <DragDropComponent items={dragDropItems} onDragEnd={handleReorder} />
        </div>
      </div>
    </div>
  )
}
